<template>
  <div>
    <v-date-picker
      v-if="noMinDate"
      :disabled-dates="disableWeekend ? { weekdays: [1, 7] } : { weekdays: [] }"
      color="green"
      :input="['DD-MM-YYYY', 'DD/MM/YYYY', 'DD.MM.YYYY']"
      :model-config="modelConfig"
      :is-range="range"
      v-model="dataDateComputed">
      <template class="input-text d-block" v-slot="{ inputValue, updateValue, showPopover }">
        <InputText
          :label="label"
          :value="parseValue(inputValue)"
          :placeholder="label"
          @focus="showPopover"
          @input="
            updateValue($event, {
              formatInput: true,
              hidePopover: true,
              debounce: 150,
            })
          "
          @change="
            updateValue($event, {
              formatInput: true,
              hidePopover: true,
            })
          "
          :rules="rules"
        />
      </template>
    </v-date-picker>
    <v-date-picker
      v-else
      :min-date='minDate'
      :disabled-dates="disableWeekend ? { weekdays: [1, 7] } : { weekdays: [] }"
      color="green"
      :input="['DD-MM-YYYY', 'DD/MM/YYYY', 'DD.MM.YYYY']"
      :model-config="modelConfig"
      :is-range="range"
      v-model="dataDateComputed">
      <template class="input-text d-block" v-slot="{ inputValue, updateValue, showPopover }">
        <InputText
          :label="label"
          :value="parseValue(inputValue)"
          :placeholder="label"
          @focus="showPopover"
          @input="
            updateValue($event, {
              formatInput: true,
              hidePopover: true,
              debounce: 150,
            })
          "
          @change="
            updateValue($event, {
              formatInput: true,
              hidePopover: true,
            })
          "
          :rules="rules"
        />
      </template>
    </v-date-picker>
  </div>
</template>

<script>
/* eslint-disable */
import InputText from "@/components/elements/inputs/text.vue";
import rules from "@/mixins/rules";
import { DateTime } from 'luxon';
import date from "../../../mixins/date";

export default {
  name: "Calendar",
  mixins: [rules, date],
  components: {
    InputText,
  },
  props: {
    value: {
      type: [String, Date, Object],
      default: '',
    },
    date: {
      type: null, // Kept for backward compatibility
    },
    label: {
      type: String,
      default: "",
    },
    noMinDate: {
      type: Boolean,
      default: false,
    },
    noDate: {
      type: Boolean,
      default: false,
    },
    disableWeekend: {
      type: Boolean,
      default: true,
    },
    range: {
      type: Boolean,
      default: false,
    },
    minDateProp: {
      type: [String, Date],
      required: false
    }
  },
  data() {
    return {
      internalDate: this.date || this.value || null, // Set date as the first priority!
      modelConfig: {
        type: 'string',
        mask: 'YYYY-MM-DD',
      },
    };
  },
  methods: {
    parseSingleDate(stringDate) {
      if (stringDate && stringDate !== 'undefined') {
        try {
          return this.parseUnknownDate(stringDate).toFormat(this.defaultDateFormat());
        } catch (e) {
          console.log(e)
          return '';
        }
      }
      return ''
    },
    parseValue(value) {
      if(!this.range) {
        return this.parseSingleDate(value)
      }
      const formattedDate = []

      if (value.start && value.start !== 'undefined') {
        const startDate = this.parseSingleDate(value.start)
        formattedDate.push(startDate)
      }

      if (value.end && value.end !== 'undefined') {
        const endDate = this.parseSingleDate(value.end)
        formattedDate.push(endDate)
      }

      return formattedDate.join(' - ')
    },

    // New method for safely emitting input events
    emitInput(val) {
      // Only emit if the value is different
      if (JSON.stringify(val) !== JSON.stringify(this.value)) {
        this.$emit('input', val);
      }
    },

    // New method for initializing default date
    initDefaultDate() {
      if (!this.range) {
        let freshDate = new Date();
        let d = new Date();
        if (freshDate.getDay() == 6) {
          d.setDate(freshDate.getDate() + 2);
        }

        if (freshDate.getDay() == 0) {
          d.setDate(freshDate.getDate() + 1);
        }

        let month = '' + (d.getMonth() + 1);
        let day = '' + d.getDate();
        let year = d.getFullYear();
        if (month.length < 2)
          month = '0' + month;
        if (day.length < 2)
          day = '0' + day;

        return [year, month, day].join('-');
      } else {
        return {
          start: new Date(),
          end: new Date()
        }
      }
    }
  },
  computed: {
    // Computed property for proper two-way binding
    dataDateComputed: {
      get() {
        return this.internalDate;
      },
      set(newVal) {
        this.internalDate = newVal;
        this.emitInput(newVal);
      }
    },
    minDate() {
      if (this.minDateProp) {
        return this.minDateProp
      }
      return DateTime.now().setZone(this.userTimezone())
    }
  },
  // In created() lifecycle hook
  created() {
    // Priority: 1. date, 2. value, 3. default
    // Change priority to give date precedence
    if (this.date && this.date !== 'undefined') {
      this.internalDate = this.date;
      console.log('Calendar initialized with date prop:', this.date);
    } else if (this.value && this.value !== 'undefined') {
      this.internalDate = this.value;
    }
  },
// In mounted() hook, ALWAYS emit the value
  mounted() {
    this.$nextTick(() => {
      console.log('Calendar mounted with values:', {
        date: this.date,
        value: this.value,
        internalDate: this.internalDate
      });

      if (this.noDate) {
        if (!this.internalDate || this.internalDate == 'undefined') {
          this.internalDate = this.date || this.value;
        }
        return;
      }

      // If we don't have a value set, initialize with default
      if (!this.internalDate || this.internalDate == 'undefined') {
        // Initialize with default value
        this.internalDate = this.initDefaultDate();
      }

      // ALWAYS emit the value on mounting, regardless of changes
      // This is key to solving the problem!
      this.$emit('input', this.internalDate);
      console.log('Calendar emitted initial value:', this.internalDate);
    })
  },
  // In watch, change the priority of the date prop
  watch: {
    // Watch changes to the date prop with higher priority
    'date': function (newVal) {
      if (newVal && newVal !== 'undefined') {
        console.log('Date prop changed to:', newVal);
        this.internalDate = newVal;
        // Emit input to notify parent component
        this.$emit('input', newVal); // Direct emit without additional checks
      }
    },

    // Also watch the value prop for standard v-model
    'value': function (newVal) {
      // Only update if date prop is not set
      if ((!this.date || this.date === 'undefined') &&
        JSON.stringify(newVal) !== JSON.stringify(this.internalDate)) {
        this.internalDate = newVal;
      }
    }
  }
};
</script>
